import { useSearch } from '@tanstack/react-router'
import { SpaceListColumnSettings, TSpaceListViewSettings } from '@valuecase/common'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  Button2,
  Divider,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input2,
  IonIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  ScrollAreaGradient,
  ScrollBar,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@valuecase/ui-components'
import EmojiPicker from 'emoji-picker-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSpaceListView } from '../../context/SpaceListViewContext'

type SpaceListViewSettings = SpaceListColumnSettings & {
  name: string
  emoji: string
  isDefault: boolean
}

interface ViewFormProps {
  view: TSpaceListViewSettings
  isPlaceholder?: boolean
  isCreating?: boolean
  onSubmit?: () => void
  onCancel?: () => void
  onClose?: () => void
  submitLabel?: string
}

const ViewForm = ({
  isPlaceholder,
  view,
  isCreating,
  onCancel,
  onClose,
  submitLabel,
}: ViewFormProps) => {
  const [name, setName] = useState(view.settings.name)
  const [emoji, setEmoji] = useState(view.settings.emoji)
  const { createView, updateView, views, placeholderView, setSelectedView } = useSpaceListView()
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false)

  const handleNameBlur = useCallback(async () => {
    if (name === '' || name === view.settings.name) {
      return
    }

    if (isPlaceholder && !isCreating) {
      createView({
        ...view.settings,
        name,
        emoji,
        isDefault: true,
      })
      return
    }

    if (isPlaceholder && isCreating) {
      return
    }

    await updateView({
      viewId: view.id,
      settings: {
        ...view.settings,
        name,
        emoji,
      },
    })
  }, [isPlaceholder, isCreating, updateView, view.id, view.settings, name, emoji, createView])

  const handleEmojiChange = useCallback(
    async (newEmoji: string) => {
      setEmoji(newEmoji)

      if (newEmoji === view.settings.emoji) {
        return
      }

      if (isPlaceholder && !isCreating) {
        await createView({
          ...view.settings,
          emoji: newEmoji,
          isDefault: true,
        })
        setEmojiPickerOpen(false)
        return
      }

      if (isPlaceholder && isCreating) {
        setEmojiPickerOpen(false)
        return
      }

      await updateView({
        viewId: view.id,
        settings: {
          ...view.settings,
          name: name ?? view.settings.name,
          emoji: newEmoji,
        },
      })
      setEmojiPickerOpen(false)
    },
    [
      isPlaceholder,
      isCreating,
      updateView,
      view.id,
      view.settings,
      name,
      createView,
      setEmojiPickerOpen,
    ],
  )

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value
      setName(newName)
    },
    [setName],
  )

  const handleSubmit = useCallback(async () => {
    if (isPlaceholder && isCreating) {
      if (views?.length === 0) {
        // In the users perspective, they are creating a second view, but in reality, they are creating their first view.
        // This is because the initial placeholder view is fake and only on the client. So if they decide to create a new view without editing the placeholder,
        // we create 2 views.
        await createView({
          ...placeholderView.settings,
          isDefault: true,
        })
        const newView = await createView({
          ...view.settings,
          name,
          emoji,
          isDefault: false,
        })
        if (!newView) {
          return
        }
        setSelectedView(newView.id)
        onClose?.()
        return
      }

      const newView = await createView({
        ...view.settings,
        name,
        emoji,
        isDefault: false,
      })
      if (!newView) {
        return
      }
      setSelectedView(newView.id)
      onClose?.()
      return
    }
  }, [
    isPlaceholder,
    isCreating,
    views?.length,
    createView,
    view.settings,
    name,
    emoji,
    onClose,
    placeholderView.settings,
    setSelectedView,
  ])

  return (
    <div className='flex flex-col h-full rounded-md shadow-sm w-60'>
      <div className='flex gap-2 p-4 bg-white rounded-md'>
        <Popover open={emojiPickerOpen} onOpenChange={setEmojiPickerOpen}>
          <PopoverTrigger>
            <div
              className='flex items-center justify-center border border-grey-s2 rounded-md bg-blue-s1 min-w-9 min-h-9 cursor-pointer relative'
              onClick={() => {
                setEmojiPickerOpen(true)
              }}
            >
              {emoji ? (
                <>
                  <span className='flex items-center justify-center h-[14px] w-[14px]'>
                    {emoji}
                  </span>
                  <IonIcon
                    name='close-circle'
                    className='absolute h-3 w-3 -top-1.5 -right-1.5 text-grey-s5 hover:text-grey-s4 cursor-pointer transition-colors'
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEmojiChange('')
                    }}
                  />
                </>
              ) : (
                <IonIcon name='happy-outline' className='text-blue-s4 w-4 h-4' />
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent className='p-0' hideClose>
            <EmojiPicker
              open={true}
              onEmojiClick={(e) => {
                handleEmojiChange(e.emoji)
              }}
            />
          </PopoverContent>
        </Popover>

        <Input2
          autoFocus={true}
          placeholder='View name'
          value={name}
          onChange={handleNameChange}
          onBlur={handleNameBlur}
          onKeyDown={(e) => {
            // Stop propagation of the keydown event to prevent parent dropdown menu from typeheading the options in the list.
            e.stopPropagation()
            if (e.key === 'Enter' && name !== '') {
              handleNameBlur()
            }
          }}
        />
      </div>
      {isCreating && (
        <>
          <Divider />
          <div className='flex gap-2 p-4 bg-white rounded-md items-center'>
            <Button2 variant={'ghost'} onClick={onCancel}>
              Cancel
            </Button2>
            <Button2
              className='w-full'
              leadingIcon='checkmark'
              onClick={handleSubmit}
              disabled={!name}
            >
              {submitLabel || 'Create view'}
            </Button2>
          </div>
        </>
      )}
    </div>
  )
}

interface SpaceViewTabProps {
  view: TSpaceListViewSettings
  onSelect: () => void
  onUpdate: (params: { viewId: string; settings: Partial<SpaceListViewSettings> }) => void
  onCreateDefault?: (settings: Partial<SpaceListViewSettings>) => void
  onDuplicate?: (viewId: string) => void
  onSetDefault?: (viewId: string) => void
  onDelete?: (viewId: string) => void
  isPlaceholder?: boolean
  initialEditOpen?: boolean
  isSelected?: boolean
}

const SpaceViewTab = ({
  view,
  onSelect,
  onDuplicate,
  onSetDefault,
  onDelete,
  isPlaceholder = false,
  initialEditOpen = false,
  isSelected = false,
}: SpaceViewTabProps) => {
  const [isEditing, setIsEditing] = useState(initialEditOpen)

  useEffect(() => {
    setIsEditing(initialEditOpen)
  }, [initialEditOpen])

  const handleDuplicate = useCallback(() => {
    if (onDuplicate) {
      onDuplicate(view.id)
    }
  }, [onDuplicate, view.id])

  return (
    <DropdownMenu
      open={isEditing}
      onOpenChange={(open) => {
        if (!open) {
          setIsEditing(false)
        }
      }}
    >
      <DropdownMenuTrigger
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        className='p-0'
        asChild
      >
        <div onClick={onSelect}>
          <TabsList className='h-full border-none p-0'>
            <TabsTrigger value={view.id} className='pb-2 px-1 pr-0 pt-0 h-6 text-grey-s5'>
              <div className='flex items-center gap-2'>
                {view.settings.emoji && <span>{view.settings.emoji}</span>}
                <span className='font-semibold'>{view.settings.name}</span>
                <Tooltip key={view.id}>
                  {isSelected && (
                    <TooltipTrigger asChild>
                      <Button2
                        variant={'plain-highlighted'}
                        size={'x-small'}
                        leadingIcon='c_edit'
                        onClick={(e) => {
                          e.stopPropagation()
                          setIsEditing(true)
                        }}
                      />
                    </TooltipTrigger>
                  )}
                  <TooltipContent side='top'>Edit</TooltipContent>
                </Tooltip>
              </div>
            </TabsTrigger>
          </TabsList>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='p-0'>
        <ViewForm view={view} isPlaceholder={isPlaceholder} onClose={() => setIsEditing(false)} />
        <div className='flex flex-col p-3'>
          <DropdownMenuItem icon={'copy-outline'} onClick={handleDuplicate}>
            Duplicate
          </DropdownMenuItem>
          {!view.settings.isDefault && !isPlaceholder && (
            <DropdownMenuItem
              icon={'star-outline'}
              onClick={() => {
                if (onSetDefault) {
                  onSetDefault(view.id)
                }
              }}
            >
              Set as default view
            </DropdownMenuItem>
          )}
          {!view.settings.isDefault && !isPlaceholder && (
            <DropdownMenuItem
              icon={'trash-outline'}
              onClick={() => {
                if (onDelete) {
                  onDelete(view.id)
                }
              }}
            >
              Delete view
            </DropdownMenuItem>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const SpacesViews = () => {
  const {
    views,
    setSelectedView,
    createView,
    updateView,
    setDefaultView,
    deleteView,
    isLoading,
    handleCreateDuplicateFromPlaceholderView,
  } = useSpaceListView()
  const [isCreating, setIsCreating] = useState(false)
  const [viewToDelete, setViewToDelete] = useState<TSpaceListViewSettings | null>(null)
  const { placeholderView } = useSpaceListView()
  const scrollViewportRef = useRef<HTMLDivElement>(null)
  const [tabEditOpenOnCreate, setTabEditOpenOnCreate] = useState<string | null>(null)
  const search = useSearch({ from: '/dashboard/spaces' })

  const getNextDuplicateNumber = useCallback(
    (baseName: string) => {
      // First, check if the base name already has a number in parentheses
      const baseNameMatch = baseName.match(/^(.*?)(?:\s*\(\d+\))?$/)
      const cleanBaseName = baseNameMatch ? baseNameMatch[1] : baseName

      // Create a regex that matches the clean base name followed by any number in parentheses
      const regex = new RegExp(`^${cleanBaseName}\\s*\\((\\d+)\\)$`)

      const existingNumbers = views
        .map((view) => {
          const name = view.settings.name
          if (!name) return 0
          const match = name.match(regex)
          return match ? parseInt(match[1], 10) : 0
        })
        .filter((num) => !isNaN(num))

      if (existingNumbers.length === 0) {
        return 1
      }

      return Math.max(...existingNumbers) + 1
    },
    [views],
  )

  const viewId = useMemo(() => {
    return search.viewId ?? placeholderView.id
  }, [search.viewId, placeholderView.id])

  const handleCreateView = useCallback(async () => {
    if (!placeholderView) {
      return
    }

    await createView({
      ...placeholderView.settings,
      isDefault: false,
    })
    setIsCreating(false)
  }, [createView, placeholderView])

  const handleCancel = useCallback(() => {
    setIsCreating(false)
  }, [])

  const handleDuplicateView = useCallback(
    async (viewId: string) => {
      if (views.length === 0) {
        const secondView = await handleCreateDuplicateFromPlaceholderView()
        // Select the second view
        if (secondView) {
          setTabEditOpenOnCreate(secondView.id)
          setSelectedView(secondView.id)
        }
        return
      }

      const viewToDuplicate = views.find((v) => v.id === viewId)
      if (!viewToDuplicate || !viewToDuplicate.settings.name) {
        return
      }

      const nextNumber = getNextDuplicateNumber(viewToDuplicate.settings.name)
      const baseNameMatch = viewToDuplicate.settings.name.match(/^(.*?)(?:\s*\(\d+\))?$/)
      const cleanBaseName = baseNameMatch ? baseNameMatch[1] : viewToDuplicate.settings.name

      const settings: SpaceListViewSettings = {
        name: `${cleanBaseName} (${nextNumber})`,
        emoji: viewToDuplicate.settings.emoji ?? '',
        isDefault: false,
        visible: viewToDuplicate.settings.visible,
        order: viewToDuplicate.settings.order,
        filters: viewToDuplicate.settings.filters,
      }

      const newView = await createView(settings)
      if (!newView) {
        return
      }
      setSelectedView(newView.id)
      setTabEditOpenOnCreate(newView.id)
    },
    [
      createView,
      handleCreateDuplicateFromPlaceholderView,
      setSelectedView,
      views,
      getNextDuplicateNumber,
    ],
  )

  const handleSetDefault = useCallback(
    async (viewId: string) => {
      await setDefaultView(viewId)
    },
    [setDefaultView],
  )

  const handleDeleteView = useCallback(
    async (viewId: string) => {
      await deleteView(viewId)
    },
    [deleteView],
  )

  const handleConfirmDeleteView = useCallback((view: TSpaceListViewSettings) => {
    setViewToDelete(view)
  }, [])

  const orderedViews = useMemo(() => {
    return views.sort((a, b) => {
      if (a.settings.isDefault) return -1
      if (b.settings.isDefault) return 1
      return 0
    })
  }, [views])

  const createViewButton = useMemo(() => {
    return (
      <DropdownMenu open={isCreating} onOpenChange={setIsCreating}>
        <DropdownMenuTrigger asChild>
          <div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button2 variant={'subtle'} size={'small'} leadingIcon='add' />
              </TooltipTrigger>
              <TooltipContent>Create view</TooltipContent>
            </Tooltip>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent className='p-0'>
          <ViewForm
            view={placeholderView}
            isPlaceholder={true}
            isCreating={true}
            onSubmit={handleCreateView}
            onCancel={handleCancel}
            submitLabel='Create view'
            onClose={() => setIsCreating(false)}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }, [isCreating, setIsCreating, placeholderView, handleCreateView, handleCancel])

  return (
    <div>
      <div>
        <ScrollArea viewportRef={scrollViewportRef} className='rounded-md'>
          <div className='min-w-max h-fit pb-2'>
            <Tabs
              value={viewId}
              onValueChange={(value: string) => {
                setSelectedView(value)
              }}
              className='flex items-center'
            >
              {isLoading ? (
                <div className='flex items-center gap-6'>
                  <Skeleton className='h-9 w-24' />
                  <Skeleton className='h-9 w-24' />
                  <Skeleton className='h-9 w-24' />
                </div>
              ) : views.length === 0 ? (
                <div className='flex items-center gap-3 h-fit border-b border-grey-s2 w-full'>
                  <SpaceViewTab
                    key={placeholderView.id}
                    view={placeholderView}
                    onSelect={() => setSelectedView(placeholderView.id)}
                    onUpdate={updateView}
                    onDuplicate={handleDuplicateView}
                    isPlaceholder={true}
                    isSelected={viewId === placeholderView.id}
                  />
                  {createViewButton}
                </div>
              ) : (
                <TabsList className='flex items-center h-fit gap-6 border-b border-grey-s2 w-full rounded-none justify-start p-0'>
                  {orderedViews.map((view) => (
                    <SpaceViewTab
                      key={view.id}
                      view={view}
                      onSelect={() => {
                        setSelectedView(view.id)
                      }}
                      onUpdate={updateView}
                      onDuplicate={handleDuplicateView}
                      onSetDefault={handleSetDefault}
                      onDelete={() => handleConfirmDeleteView(view)}
                      isPlaceholder={false}
                      initialEditOpen={tabEditOpenOnCreate === view.id}
                      isSelected={viewId === view.id}
                    />
                  ))}
                  {createViewButton}
                </TabsList>
              )}
            </Tabs>
            <ScrollBar orientation='horizontal' />
            <ScrollAreaGradient scrollAreaRef={scrollViewportRef} direction='horizontal' />
          </div>
        </ScrollArea>
      </div>

      <AlertDialog open={!!viewToDelete} onOpenChange={() => setViewToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogTitle>Delete view</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this view?
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setViewToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              leadingIcon='trash'
              onClick={() => {
                if (viewToDelete) {
                  handleDeleteView(viewToDelete.id)
                }
              }}
            >
              Yes, Delete View
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default SpacesViews
